import { Component, Mixins, Prop } from 'vue-property-decorator'
import { mapActions, mapGetters } from 'vuex'
import Table from '@kwixeo/table-vue/dist/components/Table/Table'
import ServiceDelete from '../ServiceDelete/ServiceDelete.vue'
import ServiceItem from '../ServiceItem/ServiceItem.vue'
import CatalogListMixin from '../../mixins/CatalogListMixin'
import { KTable } from '@kwixeo/table-vue'
import { emitter } from '@kwixeo/interface-vue'
import { RawLocation } from 'vue-router'

@Component({
  methods: {
    ...mapActions('dynamicModals', ['openModal'])
  },
  computed: {
    ...mapGetters('user', ['can'])
  },
  components: {
    ServiceItem,
    ServiceDelete,
    KTable
  }
})
export default class ServiceList extends Mixins(CatalogListMixin) {
  openModal!: (param: KwixeoInterface.OpenModalParam) => void
  can!: (key: string, level: number) => boolean

  @Prop({ default: 'catalog-services' }) stateKey!: string

  private serviceItem: KwixeoCatalog.Service|null = null
  private serviceToDelete: KwixeoCatalog.Service|null = null

  get include (): string[] {
    return ['sale_category', 'vat_rate', 'unit', 'customFields']
  }

  get rowActions (): KwixeoTable.RowActions {
    const actions: KwixeoTable.RowActions = []

    if (this.can('catalog-services', 3)) {
      actions.push({
        icon: 'edit',
        click: (service: KwixeoCatalog.Service) => {
          this.openModal({
            key: 'catalog-service-form',
            attributes: {
              id: service.id
            },
            value: service
          })
        }
      })
    }

    if (this.can('catalog-services', 3)) {
      actions.push(
        {
          icon: 'delete',
          click: (service: KwixeoCatalog.Service): void => {
            this.serviceToDelete = service
          }
        })
    }

    if (this.can('catalog-services', 2)) {
      actions.push({
        icon: ['fas', 'clone'],
        title: this.$t('catalog.service.clone') as string,
        click: (service: KwixeoCatalog.Service) => {
          const clone = JSON.parse(JSON.stringify(service))
          delete clone.id
          this.openModal({
            key: 'catalog-service-form',
            value: clone
          })
        }
      })
    }

    return actions
  }

  get columns (): KwixeoTable.Columns {
    return [
      {
        data: 'reference',
        name: this.$t('catalog.list.reference')
      },
      {
        data: 'name',
        name: this.$t('catalog.list.name')
      },
      {
        data: 'unit_price_ht',
        name: this.$t('catalog.list.unitPriceHt'),
        render: 'currency'
      },
      {
        data: 'unit_price_tva',
        name: this.$t('catalog.list.unitPriceTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'unit_price_ttc',
        name: this.$t('catalog.list.unitPriceTtc'),
        render: 'currency',
        visible: false
      },
      {
        data: 'default_quantity',
        name: this.$t('catalog.list.defaultQuantity'),
        render: 'amount',
        visible: false
      },
      {
        data: 'total_ht',
        name: this.$t('catalog.list.totalHt'),
        render: 'currency',
        visible: false
      },
      {
        data: 'total_tva',
        name: this.$t('catalog.list.totalTva'),
        render: 'currency',
        visible: false
      },
      {
        data: 'total_ttc',
        name: this.$t('catalog.list.totalTtc'),
        render: 'currency',
        visible: false
      },
      {
        data: 'various_costs',
        name: this.$t('catalog.list.variousCosts'),
        render: 'currency',
        visible: false
      },
      {
        data: 'total_costs',
        name: this.$t('catalog.list.totalCosts'),
        render: 'currency',
        visible: false
      },
      {
        data: 'gross_margin_amount',
        name: this.$t('catalog.list.grossMarginAmount'),
        render: 'currency',
        visible: false
      },
      {
        data: 'gross_margin_rate',
        name: this.$t('catalog.list.grossMarginRate'),
        render: 'pourcent',
        visible: false
      },
      {
        data: 'markup_rate',
        name: this.$t('catalog.list.markupRate'),
        render: 'pourcent',
        visible: false
      },
      {
        data: 'sale_category.label',
        name: this.$t('catalog.list.saleCategory'),
        visible: false
      },
      {
        data: 'vat_rate.rate',
        name: this.$t('catalog.list.vatRate'),
        render: 'pourcent'
      },
      {
        data: 'unit.label',
        name: this.$t('catalog.list.unit'),
        visible: false
      },
      {
        data: 'hourly_person_cost',
        name: this.$t('catalog.list.hourlyPersonCost'),
        render: 'currency',
        visible: false
      },
      {
        data: 'products_costs',
        name: this.$t('catalog.list.productsCosts'),
        render: 'currency',
        visible: false
      },
      {
        data: 'workforce_costs',
        name: this.$t('catalog.list.workforceCosts'),
        render: 'currency',
        visible: false
      },
      {
        data: 'duration_mn',
        name: this.$t('catalog.list.durationMn'),
        render: 'integer',
        visible: false,
        sortable: false
      },
      {
        data: 'duration_hour',
        name: this.$t('catalog.list.durationHour'),
        render: 'amount',
        visible: false,
        sortable: false
      },
      {
        data: 'number_people_needed',
        name: this.$t('catalog.list.numberPeopleNeeded'),
        render: 'integer',
        visible: false
      },
      {
        data: 'status.name',
        name: this.$t('interface.activeStatus.label'),
        visible: false
      },
      {
        data: 'created_at',
        name: this.$t('interface.list.createdAt'),
        visible: false,
        render: 'datetime'
      },
      {
        data: 'updated_at',
        name: this.$t('interface.list.updatedAt'),
        visible: false,
        render: 'datetime'
      },
      ...this.customFieldTableColumns('service')
    ]
  }

  get filters (): KwixeoTable.FiltersOption {
    return [
      {
        type: 'text',
        label: this.$t('catalog.filters.reference'),
        name: 'reference'
      },
      {
        type: 'text',
        label: this.$t('catalog.filters.name'),
        name: 'name'
      },
      {
        type: 'select',
        label: this.$t('catalog.filters.saleCategory'),
        name: 'sale_category',
        multiple: true,
        ajax: '/catalog/sale_category?sort=label',
        optionLabel: 'label',
        value: 'id'
      },
      {
        type: 'select',
        label: this.$t('catalog.filters.status'),
        name: 'status',
        options: [
          { value: true, label: this.$t('interface.activeStatus.on') },
          { value: false, label: this.$t('interface.activeStatus.off') }
        ],
        optionLabel: 'label',
        value: 'value'
      },
      ...this.customFieldTableFilters('service')
    ]
  }

  get ajaxDatas (): KwixeoTable.Object {
    return {
      include: this.computedInclude.join(',')
    }
  }

  get rowClick (): KwixeoTable.RowClick|null {
    if (this.addAction) {
      return this.addAction
    } else if (this.inlineMode !== false) {
      return null
    } else {
      return (service: KwixeoCatalog.Service) => this.serviceItem = service
    }
  }

  private rowLink (service: KwixeoCatalog.Service): RawLocation {
    return {
      name: 'catalog-service-item',
      params: {
        id: String(service.id)
      }
    }
  }

  async mounted (): Promise<void> {
    emitter.on('serviceCreated', this.serviceCreated)
    emitter.on('serviceUpdated', this.serviceUpdated)
    emitter.on('serviceDeleted', this.serviceDeleted)
  }

  destroyed (): void {
    emitter.off('serviceCreated', this.serviceCreated)
    emitter.off('serviceUpdated', this.serviceUpdated)
    emitter.off('serviceDeleted', this.serviceDeleted)
  }

  private serviceCreated (service: KwixeoCatalog.Service): void {
    const table = this.$refs.table as Table
    table.addRow(service)
  }

  private serviceUpdated (event: KwixeoCatalog.ServiceUpdateEvent): void {
    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(data => data.id === event.new.id)
    if (index >= 0) {
      table.updateRow(index, event.new)
    } else {
      table.addRow(event.new)
    }
  }

  private serviceDeleted (service: KwixeoCatalog.Service): void {
    const table = this.$refs.table as Table
    const index = table.getDatas().findIndex(data => data.id === service.id)
    if (index >= 0) {
      table.deleteRow(index)
    }
  }
}
